import React, { useEffect, useRef, useState } from 'react';
import { fmt } from '../../data/metrics';
import { Delta } from './Delta';

/** Rate KPIs whose scorecard/drill delta is points, not relative %. */
export const PCT_METRIC_KEYS = new Set([
  'noshow',
  'cancel',
  'occupancy',
  'brainAutoPct',
]);

// Percentage change of `actual` vs `ref`, or null when there's no baseline.
export function relDelta(actual: number, ref: number): number | null {
  if (!ref) return null;
  return +((actual / ref - 1) * 100).toFixed(1);
}

/** Point change of a rate KPI (`value − prior`), one decimal like drill. */
export function pointDelta(
  actual: number,
  ref: number | null | undefined,
): number | null {
  if (ref == null || !Number.isFinite(actual) || !Number.isFinite(ref)) {
    return null;
  }
  return +(actual - ref).toFixed(1);
}

/** Scorecard delta: rates in points, counts as relative %. */
export function scorecardPeriodDelta(m: {
  key: string;
  value: number;
  prior: number;
}): { value: number | null; suffix: string } {
  if (PCT_METRIC_KEYS.has(m.key)) {
    return { value: pointDelta(m.value, m.prior), suffix: 'pt' };
  }
  return { value: relDelta(m.value, m.prior), suffix: '%' };
}

/** Integer KPIs use the same rounding as drill `fmt.num`. */
export function scorecardHeroText(m: {
  key: string;
  value: number;
  unit?: string;
}): string {
  if (
    m.key === 'covers' ||
    m.key === 'reservations' ||
    m.key === 'rejected' ||
    m.key === 'incoming'
  ) {
    return fmt.num(m.value);
  }
  if (m.key === 'turns' || m.unit === '×') {
    return m.value.toLocaleString('de-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  if (
    m.unit === '€' ||
    m.key === 'sellitems' ||
    m.key === 'revenue' ||
    m.key === 'avgCheck' ||
    m.key === 'revpash'
  ) {
    return m.value.toLocaleString('de-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return String(m.value);
}

/** Period vs compare when compare is on; last two spark buckets when it is off. */
export function highlightMetricDelta(
  metric: { key: string; value: number; prior: number; series?: number[] } | undefined,
  comparable: boolean,
): { value: number | null; suffix: string; headline: number } | null {
  if (!metric) return null;
  const series = metric.series ?? [];
  if (!comparable && series.length > 1) {
    const delta = scorecardPeriodDelta({
      key: metric.key,
      value: series[series.length - 1],
      prior: series[series.length - 2],
    });
    return { ...delta, headline: series[series.length - 1] };
  }
  return { ...scorecardPeriodDelta(metric), headline: metric.value };
}

export function formatSignedDelta(
  value: number | null | undefined,
  suffix = '%',
): string {
  if (value == null || Number.isNaN(value) || Math.abs(value) < 0.05) {
    return '—';
  }
  return `${value > 0 ? '+' : ''}${value}${suffix}`;
}

// Format a single per-bucket value, matching how a metric's headline value is
// rendered for that unit.
export function fmtBucketVal(v: number, unit?: string): string {
  if (unit === '€') return fmt.eur(v);
  if (unit === '%') return `${v}%`;
  if (unit === 'm' || unit === 'min') return `${v}m`;
  if (unit === 'pp' || unit === 'pt') return `${v}pt`;
  if (unit === 'rating') return v.toFixed(2);
  if (unit === '×') return `${v}×`;
  return v.toLocaleString('de-CH');
}

// The shared "contextualized" reading of a per-bucket series: the latest bucket
// (the bold figure), the one before it (the period-over-period baseline), the
// whole-frame total, the start→end move, and the peak/low bucket indexes. Mirrors
// exactly what the scorecard tiles surface, so every section reads the same way.
export type TrendStats = {
  hasSeries: boolean;
  latest: number;
  prev: number;
  delta: number | null;
  total: number;
  overall: number | null;
  peakI: number;
  lowI: number;
};

export function trendStats(series: number[] | undefined): TrendStats {
  const s = series ?? [];
  const n = s.length;
  const hasSeries = n > 1;
  let peakI = 0;
  let lowI = 0;
  if (hasSeries) {
    s.forEach((v, i) => {
      if (v > s[peakI]) peakI = i;
      if (v < s[lowI]) lowI = i;
    });
  }
  return {
    hasSeries,
    latest: n ? s[n - 1] : 0,
    prev: n > 1 ? s[n - 2] : 0,
    delta: hasSeries ? relDelta(s[n - 1], s[n - 2]) : null,
    total: s.reduce((a, v) => a + v, 0),
    overall: hasSeries ? relDelta(s[n - 1], s[0]) : null,
    peakI,
    lowI,
  };
}

// Short x-axis tick when the column is too narrow for the full bucket label.
export function abbrevLabel(label: string): string {
  const s = (label ?? '').trim();
  const dash = s.indexOf(' – ');
  if (dash > 0) {
    const startPart = s.slice(0, dash);
    const endPart = s.slice(dash + 3);
    const endMonth = endPart.replace(/^\d+\s*/, '');
    if (startPart.endsWith(endMonth)) {
      const startDay = startPart.split(' ')[0];
      return `${startDay}–${endPart}`;
    }
    if (s.length <= 14) return s;
    return `${startPart.split(' ')[0]}–${endPart.split(' ').slice(-2).join(' ')}`;
  }
  const sp = s.indexOf(" '");
  if (sp > 0) return s.slice(0, sp);
  if (s.length > 6) return `${s.slice(0, 5)}…`;
  return s;
}

// Signed percentage for tooltips (no arrow icon).
function fmtTrendPct(value: number | null): string | null {
  if (value == null || Number.isNaN(value)) return null;
  const sign = value > 0 ? '+' : '';
  return `${sign}${value}% trend`;
}

/** Above this count, flex bars collapse to sub-pixel width in a scorecard tile. */
export const MINI_TREND_LINE_AT = 40;

/** Cap drawn vertices so a year of days doesn't render as noise. */
const LINE_DRAW_POINTS = 36;

export function usesLineTrend(n: number): boolean {
  return n >= MINI_TREND_LINE_AT;
}

/** Average `values` into at most `target` buckets; keep the true latest point. */
function downsampleForSparkline(values: number[], target: number): number[] {
  const n = values.length;
  if (n <= target) return values;
  const out: number[] = new Array(target);
  for (let i = 0; i < target; i++) {
    const start = Math.floor((i / target) * n);
    const end = Math.max(start + 1, Math.floor(((i + 1) / target) * n));
    let sum = 0;
    for (let j = start; j < end; j++) sum += values[j];
    out[i] = sum / (end - start);
  }
  out[target - 1] = values[n - 1];
  return out;
}

function sparkY(v: number, max: number, h: number, padY: number): number {
  return padY + (1 - Math.abs(v) / max) * (h - padY * 2);
}

function sparkX(i: number, n: number, w: number): number {
  return n === 1 ? w / 2 : (i / (n - 1)) * w;
}

// Dense series: downsampled SVG line + soft area (raw 365-point paths look like static).
function DenseTrendLine({
  values,
  labels,
  unit,
  compact = false,
  embedded = false,
  peakI,
  lowI,
  currentI,
}: {
  values: number[];
  labels: string[];
  unit?: string;
  compact?: boolean;
  embedded?: boolean;
  peakI?: number;
  lowI?: number;
  currentI?: number;
}) {
  const n = values.length;
  const drawn = downsampleForSparkline(values, LINE_DRAW_POINTS);
  const dn = drawn.length;
  const max = Math.max(
    ...values.map(v => Math.abs(v)),
    ...drawn.map(v => Math.abs(v)),
    0.0001,
  );
  const w = 120;
  const h = compact ? 22 : 34;
  const padY = 3;
  const pts = drawn.map((v, i) => ({
    x: sparkX(i, dn, w),
    y: sparkY(v, max, h, padY),
  }));
  const lineD = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(' ');
  const areaD =
    pts.length > 0
      ? `${lineD} L${pts[pts.length - 1].x.toFixed(2)},${h} L${pts[0].x.toFixed(2)},${h} Z`
      : '';
  const markPeak = peakI != null && lowI != null && peakI !== lowI;
  const markCurrent = currentI != null && currentI >= 0 && currentI < n;
  // Markers as HTML overlays — SVG circles stretch into ovals under
  // preserveAspectRatio="none".
  const markStyle = (i: number): React.CSSProperties => ({
    left: `${(sparkX(i, n, w) / w) * 100}%`,
    top: `${(sparkY(values[i], max, h, padY) / h) * 100}%`,
  });
  const overall = fmtTrendPct(relDelta(values[n - 1], values[0]));
  const tipParts = [
    `${labels[0] ?? ''} → ${labels[n - 1] ?? ''}`,
    overall,
    markPeak
      ? `peak ${fmtBucketVal(values[peakI!], unit)} on ${labels[peakI!] ?? ''}`
      : null,
    markPeak
      ? `low ${fmtBucketVal(values[lowI!], unit)} on ${labels[lowI!] ?? ''}`
      : null,
    markCurrent
      ? `latest ${fmtBucketVal(values[currentI!], unit)}`
      : null,
    `smoothed from ${n} buckets`,
  ].filter(Boolean);
  const tip = tipParts.join(' · ');

  return (
    <div
      className={`sc-trend line${compact ? ' compact' : ''}${embedded ? ' embedded' : ''}`}
      data-tip={tip}
    >
      <div className="sc-trend-line-host">
        <svg
          className="sc-trend-line"
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={tip}
        >
          {areaD ? <path className="sc-trend-line-area" d={areaD} /> : null}
          <path className="sc-trend-line-path" d={lineD} />
        </svg>
        {markPeak && (
          <>
            <span
              className="sc-trend-line-mark is-peak"
              style={markStyle(peakI!)}
              title={`Peak · ${labels[peakI!] ?? ''}: ${fmtBucketVal(values[peakI!], unit)}`}
            />
            <span
              className="sc-trend-line-mark is-low"
              style={markStyle(lowI!)}
              title={`Low · ${labels[lowI!] ?? ''}: ${fmtBucketVal(values[lowI!], unit)}`}
            />
          </>
        )}
        {markCurrent && (
          <span
            className="sc-trend-line-mark is-current"
            style={markStyle(currentI!)}
            title={`Latest · ${labels[currentI!] ?? ''}: ${fmtBucketVal(values[currentI!], unit)}`}
          />
        )}
      </div>
    </div>
  );
}

// A metric broken down per aggregation bucket, rendered as a compact bar strip.
// Bars fade in by recency so the latest bucket reads as "now". Per-bucket numbers
// sit over each bar when the columns are wide enough; otherwise they fall back to
// an instant hover tip on each column. `compact` trims chrome for dense rows.
// When the series is too dense for bars (~40+), switches to a line sparkline.
export function MiniTrend({
  values,
  secondaryValues,
  labels,
  unit,
  secondaryUnit,
  primaryLabel,
  secondaryLabel,
  compact = false,
  embedded = false,
  peakI,
  lowI,
  currentI,
}: {
  values: number[];
  /** Optional paired series (e.g. cancel beside no-show). */
  secondaryValues?: number[];
  labels: string[];
  unit?: string;
  secondaryUnit?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  compact?: boolean;
  embedded?: boolean;
  peakI?: number;
  lowI?: number;
  /** Highlight the "current" bucket (usually the last bar). */
  currentI?: number;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [slot, setSlot] = useState(0);
  const n = values.length;
  const paired =
    !!secondaryValues &&
    secondaryValues.length === n &&
    n >= 2;
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () =>
      setSlot(n ? Math.max(0, (el.clientWidth - 4 * (n - 1)) / n) : 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [n]);

  if (n < 2) return null;
  // Paired series stay as bars so both metrics remain readable.
  if (!paired && usesLineTrend(n)) {
    return (
      <DenseTrendLine
        values={values}
        labels={labels}
        unit={unit}
        compact={compact}
        embedded={embedded}
        peakI={peakI}
        lowI={lowI}
        currentI={currentI}
      />
    );
  }
  const max = Math.max(
    ...values.map(v => Math.abs(v)),
    ...(paired ? secondaryValues!.map(v => Math.abs(v)) : []),
    0.0001,
  );
  const valStrs = values.map(v => fmtBucketVal(v, unit));
  const valStrs2 = paired
    ? secondaryValues!.map(v => fmtBucketVal(v, secondaryUnit ?? unit))
    : [];
  const maxValLen = Math.max(
    ...valStrs.map(s => s.length),
    ...valStrs2.map(s => s.length),
    1,
  );
  const maxLabLen = Math.max(
    0,
    ...labels.slice(0, n).map(s => (s ?? '').length),
  );
  const needVal = maxValLen * 5 + 3;
  const needLab = maxLabLen * 5 + 3;
  const measured = slot > 0;
  const showVals = compact
    ? false
    : measured
      ? slot >= needVal
      : n <= 6;
  const showLabs = compact
    ? false
    : measured
      ? slot >= needLab
      : n <= 6;
  const showShortLabs = !showLabs && measured && slot >= 18;
  const markPeak = peakI != null && lowI != null && peakI !== lowI;
  const markCurrent = currentI != null && currentI >= 0 && currentI < n;
  return (
    <div
      className={`sc-trend${showVals ? '' : ' dense'}${compact ? ' compact' : ''}${embedded ? ' embedded' : ''}${paired ? ' paired' : ''}`}
      ref={trackRef}
    >
      {values.map((v, i) => {
        const v2 = paired ? secondaryValues![i] : undefined;
        const hPct = Math.max(6, (Math.abs(v) / max) * 100);
        const hPct2 =
          v2 != null ? Math.max(6, (Math.abs(v2) / max) * 100) : null;
        const isCurrent = markCurrent && i === currentI;
        const op = embedded
          ? isCurrent
            ? 1
            : 0.72
          : n > 1
            ? 0.4 + 0.6 * (i / (n - 1))
            : 1;
        const primaryTip = `${valStrs[i]}${
          primaryLabel ? ` ${primaryLabel}` : ''
        }`;
        const secondaryTip =
          paired && v2 != null
            ? `${valStrs2[i]}${secondaryLabel ? ` ${secondaryLabel}` : ''}`
            : '';
        const tip = [
          labels[i] ?? '',
          primaryTip,
          secondaryTip,
          isCurrent ? 'current' : '',
        ]
          .filter(Boolean)
          .join(' · ');
        const isPeak = !isCurrent && markPeak && i === peakI;
        const isLow = !isCurrent && markPeak && i === lowI;
        const colClass = [
          isCurrent ? 'is-current' : '',
          isPeak ? 'is-peak' : '',
          isLow ? 'is-low' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <div
            className={`sc-tcol${colClass ? ` ${colClass}` : ''}`}
            key={i}
            data-tip={tip}
          >
            {showVals && <span className="sc-tval">{valStrs[i]}</span>}
            <span className={`sc-ttrack${paired ? ' sc-ttrack-paired' : ''}`}>
              <span
                className="sc-tbar"
                style={{ height: `${hPct}%`, opacity: op }}
              />
              {paired && hPct2 != null && (
                <span
                  className="sc-tbar sc-tbar-2"
                  style={{ height: `${hPct2}%`, opacity: op }}
                />
              )}
            </span>
            {showLabs && (
              <span className="sc-tlab">{labels[i] ?? ''}</span>
            )}
            {showShortLabs && (
              <span className="sc-tlab sc-tlab-short" title={labels[i] ?? ''}>
                {abbrevLabel(labels[i] ?? '')}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Scorecard trend footer: chart only; overall trend on the strip above the chart.
export function ScorecardTrendPanel({
  values,
  labels,
  unit,
  chrome = 'full',
}: {
  values: number[];
  labels: string[];
  unit?: string;
  invert?: boolean;
  /** `minimal` hides axis labels + Peak/Low/Latest legend (tooltips stay). */
  chrome?: 'full' | 'minimal';
}) {
  const st = trendStats(values);
  const n = values.length;
  if (n < 2) return null;

  const asLine = usesLineTrend(n);
  const trendStr = fmtTrendPct(st.overall);
  const spanLabel = `${labels[0]} → ${labels[n - 1]}`;
  const minimal = chrome === 'minimal';
  // Bar mode: thin tip strip above the columns. Line mode: tip lives on the
  // sparkline itself with peak/low/latest detail.
  const panelTip =
    !asLine && trendStr ? `${trendStr} · ${spanLabel}` : undefined;

  return (
    <div className={`sc-trend-panel${minimal ? ' is-minimal' : ''}`}>
      {panelTip ? (
        <div
          className="sc-trend-tip-zone"
          data-tip={panelTip}
          aria-label={panelTip}
        />
      ) : null}
      <MiniTrend
        values={values}
        labels={labels}
        unit={unit}
        embedded
        compact={minimal}
        peakI={st.peakI}
        lowI={st.lowI}
        currentI={n - 1}
      />
      {!minimal && (
        <div className="sc-trend-legend" aria-hidden="true">
          {asLine ? (
            <span className="sc-trend-legend-item is-line">
              Smoothed trend · {n} buckets
            </span>
          ) : (
            <>
              <span className="sc-trend-legend-item is-peak">Peak</span>
              <span className="sc-trend-legend-item is-low">Low</span>
              <span className="sc-trend-legend-item is-current">Latest</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// One contextualized breakdown row, shared by the Regions / Sell-by-region /
// Capacity sections. Same model as a scorecard tile, laid out horizontally:
// the latest bucket (bold) with its period-over-period delta, a per-bucket
// mini-trend, and the whole-frame total over its concrete date span. Labels are
// real bucket dates (e.g. "May '26", "Jun '25 – May '26"), never relative phrases.
export function TrendRow({
  name,
  sub,
  series,
  labels,
  unit,
  noun,
  invert = false,
  comparable = true,
  onClick,
}: {
  name: React.ReactNode;
  sub?: React.ReactNode;
  series: number[];
  labels: string[];
  unit?: string;
  // Explicit measure noun appended to the figures (e.g. "guests"), so the row
  // states what it counts. Omit when `unit` already disambiguates (€, %, …).
  noun?: string;
  invert?: boolean;
  comparable?: boolean;
  onClick?: () => void;
}) {
  const st = trendStats(series);
  const n = labels.length;
  const latestLabel = n ? labels[n - 1] : '';
  const prevLabel = n > 1 ? labels[n - 2] : '';
  const rangeLabel = n > 1 ? `${labels[0]} – ${labels[n - 1]}` : '';
  const nounSuffix = noun ? ` ${noun}` : '';
  return (
    <div
      className={`trow${onClick ? ' clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <div className="trow-head">
        <b>{name}</b>
        <span className="trow-sub">
          {sub != null && <>{sub} · </>}
          {fmtBucketVal(st.total, unit)}
          {nounSuffix} over {rangeLabel}
        </span>
      </div>
      <div className="trow-now">
        <span className="trow-val">
          {fmtBucketVal(comparable ? st.latest : st.total, unit)}
          {noun && <span className="trow-noun">{noun}</span>}
        </span>
        {comparable && <span className="trow-when">{latestLabel}</span>}
      </div>
      {comparable && (
        <div className="trow-delta">
          <Delta value={st.delta} invert={invert} size={12} />
          <span className="trow-vs">vs {prevLabel}</span>
        </div>
      )}
      {n >= 2 && (
        <div className="trow-spark">
          <MiniTrend values={series} labels={labels} unit={unit} compact />
        </div>
      )}
    </div>
  );
}
